import React, { useState } from 'react';
import { ChevronLeft, Filter, LayoutGrid, Mic, Plus, Search, Users } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import type { TaskItem } from '../../../../types/topic';
import { extractBaseColor, getStatusColor } from '../../../../utils/colorUtils';
import { UserFilter } from '../../../dashboard/types/dashboardTypes';
import { InlineColumnSelector } from './InlineColumnSelector';
import { ColumnMenu } from '../ColumnMenu';
import { MobileSearchInput } from './MobileSearchInput';
import { useTopicFilters } from '../../../../contexts/TopicFiltersContext';
import { useTaskColumns } from '../../../../hooks/queries/useTaskColumns';

interface MobileTaskHeaderProps {
  topicName: string;
  onBackClick: () => void;
  isRecording: boolean;
  onVoiceClick: () => void;
  onAddClick: () => void;
  columns: readonly {
    id: string;
    label: string;
    color?: string;
  }[];
  activeColumn: TaskItem['status'];
  onColumnSelect: (columnId: TaskItem['status']) => void;
  // Filter props
  userFilters?: UserFilter[];
  viewMode?: 'board' | 'timeline';
  onOpenTaskFilters?: () => void;
  onOpenUserFilters?: () => void;
  toggleViewMode?: () => void;
  hasCollaborators?: boolean;
  // Column management props
  onDeleteColumnClick?: (columnId: string) => void;
  onEditColumnNameClick?: (columnId: string) => void;
  onColorSelect?: (columnId: string, color: string) => void;
  onAddColumnAfter?: (columnId: string) => void;
  onMoveColumnLeft?: (columnId: string) => void;
  onMoveColumnRight?: (columnId: string) => void;
  // Search props
  onToggleSearch?: () => void;
  // Topic ID for filter persistence
  topicId: string;
}

export function MobileTaskHeader({
  onBackClick,
  isRecording,
  onVoiceClick,
  onAddClick,
  columns,
  activeColumn,
  onColumnSelect,
  userFilters,
  viewMode = 'board',
  onOpenTaskFilters,
  onOpenUserFilters,
  toggleViewMode,
  hasCollaborators,
  onDeleteColumnClick,
  onEditColumnNameClick,
  onColorSelect,
  onAddColumnAfter,
  onMoveColumnLeft,
  onMoveColumnRight,
  onToggleSearch,
  topicId,
}: MobileTaskHeaderProps) {
  // State to manage column selector visibility
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [showSearchInput, setShowSearchInput] = useState(false);

  // Get search query from context to check if it's active
  const { searchQuery } = useTopicFilters();
  const hasActiveSearch = !!searchQuery?.trim();

  // Format columns for color utils
  const columnsForColorUtil = columns.map(col => {
    const baseColor = extractBaseColor(col.color);
    const colorName = baseColor || 'gray';
    return {
      status: col.id,
      color: colorName,
    };
  });

  // Get current active column data
  const activeColumnData = columns.find(col => col.id === activeColumn);

  // Get the full column data using React Query for use with the ColumnMenu
  const { data: taskColumns = [] } = useTaskColumns(topicId);
  const currentColumn = taskColumns.find(col => col.status === activeColumn);

  // Get background color for active column
  const getActiveColumnBgColor = () => {
    const baseColor = getStatusColor(activeColumn, columnsForColorUtil, 'bg')
      .replace('bg-', '')
      .replace('-100', '-500');

    return `bg-${baseColor}`;
  };

  // Handle column selection
  const handleColumnButtonClick = () => {
    setShowColumnSelector(true);
  };

  // Get all active filters from TopicFiltersContext
  const { showUnscheduledOnly, showUnassignedOnly, showHighlightedOnly, selectedTags } =
    useTopicFilters();

  // Calculate active task filters count using context values
  const activeTaskFiltersCount =
    (showUnscheduledOnly ? 1 : 0) +
    (hasCollaborators && showUnassignedOnly ? 1 : 0) +
    (showHighlightedOnly ? 1 : 0) +
    (selectedTags.length > 0 ? 1 : 0);

  // Get count of active user filters
  const activeUserFiltersCount = userFilters?.filter(filter => filter.active).length || 0;

  // Toggle search input visibility
  const handleToggleSearch = () => {
    setShowSearchInput(!showSearchInput);
    if (onToggleSearch) onToggleSearch();
  };

  return (
    <div className="flex items-center h-16 bg-[var(--content-bg-color)] relative overflow-hidden">
      {/* Mobile Search Input - Full width overlay when visible */}
      <MobileSearchInput isVisible={showSearchInput} onClose={() => setShowSearchInput(false)} />

      {/* Left side elements - slide left out of screen when selector is shown */}
      <div
        className={cn(
          'flex items-center absolute left-4 transition-all duration-300 z-20',
          showColumnSelector || showSearchInput
            ? 'opacity-0 -translate-x-full'
            : 'opacity-100 translate-x-0'
        )}
      >
        {/* Left side container with back button and column selector */}
        <div className="flex-none flex items-center gap-2">
          {/* Back button */}
          <button
            onClick={onBackClick}
            className="p-2 text-gray-600 hover:text-gray-900 bg-gray-200 rounded-lg"
          >
            <ChevronLeft size={20} />
          </button>

          {/* Column selector button with column color */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleColumnButtonClick}
              className={cn(
                'h-9 px-3 rounded-lg text-sm font-medium text-white flex items-center gap-1',
                getActiveColumnBgColor()
              )}
            >
              <span>{activeColumnData?.label || 'Select'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Right side elements - slide right out of screen when selector is shown */}
      <div
        className={cn(
          'flex items-center gap-2 absolute right-4 transition-all duration-300 z-20',
          showColumnSelector || showSearchInput
            ? 'opacity-0 translate-x-full'
            : 'opacity-100 translate-x-0'
        )}
      >
        {/* Column Menu Button - Added for mobile */}
        {currentColumn &&
          onDeleteColumnClick &&
          onColorSelect &&
          onAddColumnAfter &&
          onMoveColumnLeft &&
          onMoveColumnRight && (
            <div className="relative">
              <ColumnMenu
                column={currentColumn}
                isFirst={columns.findIndex(c => c.id === currentColumn.id) === 0}
                isLast={columns.findIndex(c => c.id === currentColumn.id) === columns.length - 1}
                onDeleteClick={onDeleteColumnClick}
                onEditNameClick={onEditColumnNameClick}
                onColorSelect={onColorSelect}
                onAddColumn={onAddColumnAfter}
                onMoveLeft={onMoveColumnLeft}
                onMoveRight={onMoveColumnRight}
              />
            </div>
          )}

        {/* Search Button */}
        <button
          onClick={handleToggleSearch}
          className={cn(
            'h-9 w-9 flex items-center justify-center rounded-lg transition-colors relative',
            hasActiveSearch
              ? 'bg-blue-100 text-blue-600'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          )}
          title="Search"
        >
          <Search size={16} />
          {hasActiveSearch && (
            <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-4 h-4 text-xs font-bold bg-blue-500 text-white rounded-full" />
          )}
        </button>

        {/* Task Filter Button */}
        {onOpenTaskFilters && (
          <button
            onClick={onOpenTaskFilters}
            className={cn(
              'h-9 w-9 flex items-center justify-center rounded-lg transition-colors relative',
              activeTaskFiltersCount > 0
                ? 'bg-blue-100 text-blue-600'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            )}
            title="Filter"
          >
            <Filter size={16} />
            {activeTaskFiltersCount > 0 && (
              <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-4 h-4 text-xs font-bold bg-blue-500 text-white rounded-full">
                {activeTaskFiltersCount}
              </span>
            )}
          </button>
        )}

        {/* User Filter Button */}
        {hasCollaborators && userFilters?.length > 0 && onOpenUserFilters && (
          <button
            onClick={onOpenUserFilters}
            className={cn(
              'h-9 w-9 flex items-center justify-center rounded-lg transition-colors relative',
              activeUserFiltersCount > 0
                ? 'bg-blue-100 text-blue-600'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            )}
            title="People"
          >
            <Users size={16} />
            {activeUserFiltersCount > 0 && (
              <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-4 h-4 text-xs font-bold bg-blue-500 text-white rounded-full">
                {activeUserFiltersCount}
              </span>
            )}
          </button>
        )}

        {/* View Mode Toggle */}
        {toggleViewMode && (
          <button
            onClick={toggleViewMode}
            className={cn(
              'h-9 w-9 flex items-center justify-center rounded-lg transition-colors',
              'bg-blue-100 text-blue-600'
            )}
            title={`Switch to ${viewMode === 'board' ? 'calendar' : 'board'} view`}
          >
            {viewMode === 'board' ? <LayoutGrid size={16} /> : <LayoutGrid size={16} />}
          </button>
        )}

        {/* Voice recording button - now green */}
        <button
          onClick={onVoiceClick}
          className={cn(
            'h-9 w-9 flex items-center justify-center rounded-lg transition-colors text-white',
            isRecording ? 'bg-red-500' : 'bg-green-500 hover:bg-green-600'
          )}
          title={isRecording ? 'Stop recording' : 'Start voice recording'}
        >
          <Mic size={16} className={cn(isRecording && 'animate-pulse')} />
        </button>

        {/* Add task button - now black */}
        <button
          onClick={onAddClick}
          className="h-9 w-9 flex items-center justify-center rounded-lg bg-black text-white hover:bg-gray-800 transition-colors"
          title="Add task"
        >
          <Plus size={16} />
        </button>
      </div>

      {/* Column selector that expands in-place with a growing animation */}
      <div
        className={cn(
          'absolute inset-0 flex items-center justify-center transition-all duration-300 overflow-hidden z-30',
          showColumnSelector ? 'w-full opacity-100' : 'w-0 opacity-0 pointer-events-none'
        )}
      >
        <InlineColumnSelector
          columns={columns}
          activeColumn={activeColumn}
          onColumnSelect={onColumnSelect}
          onClose={() => setShowColumnSelector(false)}
        />
      </div>
    </div>
  );
}
